import { useCallback, useMemo } from 'react'
import { useAuthStore } from '@stores/authStore'
import {
  hasPermission as checkPermission,
  hasAnyPermission as checkAnyPermission,
  normalizeRole,
  PERMISSION_MATRIX,
} from '@/config/permissions'
import type { Role, Resource, Action } from '@/config/permissions'

/**
 * Hook for checking user permissions
 * Memoizes checks to prevent unnecessary re-renders
 */
export function usePermission() {
  const user = useAuthStore((state) => state.user)
  const role = useMemo(
    () => (user?.role ? normalizeRole(user.role) : 'viewer'),
    [user?.role]
  ) as Role

  /**
   * Check if current user can perform action on resource
   * For :own permissions, requires ownerId to match user.id
   * @param resource - The resource to check
   * @param action - The action to perform
   * @param ownerId - Optional owner ID for :own checks
   */
  const can = useCallback(
    (resource: Resource, action: Action, ownerId?: number): boolean => {
      if (!user) return false
      const isOwner = ownerId !== undefined ? ownerId === user.id : undefined
      return checkPermission(role, resource, action, isOwner)
    },
    [user, role]
  )

  /**
   * Check if user has access to a resource (for navigation visibility)
   * Returns true if user has EITHER full permission OR :own permission
   * Use this for showing/hiding nav items
   */
  const canAccess = useCallback(
    (resource: Resource, action: Action): boolean => {
      if (!user) return false
      return checkAnyPermission(role, resource, action)
    },
    [user, role]
  )

  /**
   * Check if user has any of the specified permissions
   */
  const canAny = useCallback(
    (checks: Array<{ resource: Resource; action: Action }>): boolean => {
      return checks.some(({ resource, action }) => can(resource, action))
    },
    [can]
  )

  /**
   * Check if user has all of the specified permissions
   */
  const canAll = useCallback(
    (checks: Array<{ resource: Resource; action: Action }>): boolean => {
      return checks.every(({ resource, action }) => can(resource, action))
    },
    [can]
  )

  /**
   * Get all permissions for current role (for debugging)
   */
  const permissions = useMemo(() => {
    const result: string[] = []
    for (const [resource, roles] of Object.entries(PERMISSION_MATRIX)) {
      const actions = roles[role] || []
      actions.forEach((action) => result.push(`${resource}:${action}`))
    }
    return result
  }, [role])

  return {
    can,
    canAccess,
    canAny,
    canAll,
    role,
    permissions,
    isAuthenticated: !!user,
  }
}
